import React, { useState, useRef } from "react";
import styles from "./CaseStudySection.module.css";
import { MdKeyboardArrowRight } from "react-icons/md";
import Button from "../../Button/Button";
import { FaTerminal } from "react-icons/fa6";
import smartMedicsCase from "../../../assets/images/Case study section - preview (SmartMedics).png";
import dilCase from "../../../assets/images/Case study section - preview (Diagnozuj i Lecz).png";
import { useNavigate } from "react-router-dom";
import { isSmallScreen } from "../../../utils/screen";
import { useTranslation } from "../../../hooks/useTranslation";

interface CaseStudy {
  listTitle?: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  image: string;
  buttonText: React.ReactNode;
}

interface CaseStudySectionProps {
  title: React.ReactNode;
}

const CaseStudySection: React.FC<CaseStudySectionProps> = ({ title }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const caseStudies: CaseStudy[] = [
    {
      listTitle: t("caseStudy.listTitle.1"),
      title: t("caseStudy.title.1"),
      description: t("caseStudy.description.1"),
      image: dilCase,
      buttonText: t("caseStudy.button"),
    },
    {
      title: t("caseStudy.title.2"),
      description: t("caseStudy.description.2"),
      image: smartMedicsCase,
      buttonText: t("caseStudy.button"),
    },
  ];

  const handleClick = (index: number) => {
    setActiveIndex(index);
    // Scroll to right card on smaller devices
    if (isSmallScreen() && containerRef.current) {
      const elementPosition = containerRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 64;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const navigateToCaseStudy = (index: number) => {
    const tabIds = ["diagnozujILecz", "smartMedics"];
    const currentPath = window.location.pathname;
    
    // If already on the case studies page, just update the hash and scroll to content
    if (currentPath === '/realizacje') {
      // Update URL without navigating away
      window.history.pushState({}, '', `/realizacje#${tabIds[index]}`);
      // Find the content section and scroll to it with offset
      const contentSection = document.getElementById('case-study-content');
      if (contentSection) {
        const elementPosition = contentSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 64;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Navigate to the case study page with the appropriate hash
      navigate(`/realizacje#${tabIds[index]}`);
    }
  };

  return (
    <section className={styles.caseStudySection}>
      <div className={styles.container}>
        {/* Left Card */}
        <div className={styles.leftCard}>
          <div className={styles.cardTitleContainer}>
            <FaTerminal size={isSmallScreen() ? 44 : 54} />
            <div className={styles.cardText}>
              <h2 className={styles.title}>{title}</h2>
              <p className={styles.subtitle}>
                {t("caseStudy.subtitle")}
              </p>
            </div>
          </div>
          <ul className={styles.caseStudyList}>
            {caseStudies.map((study, index) => (
              <li
                key={index}
                className={`${styles.listItem} ${index === activeIndex ? styles.active : ""}`}
                onClick={() => handleClick(index)}
              >
                {study.listTitle || study.title}
                <MdKeyboardArrowRight size={24} />
              </li>
            ))}
          </ul>
        </div>

        {/* Right Card */}
        <div className={styles.rightCard} ref={containerRef}>
          <img 
            src={caseStudies[activeIndex].image} 
            alt={'Case study image'} 
            className={styles.image} 
          />
          <div className={styles.content}>
            <h3 className={styles.caseTitle}>{caseStudies[activeIndex].title}</h3>
            <p className={styles.caseDescription}>{caseStudies[activeIndex].description}</p>
            <Button variant={"outline"} className={styles.button}
              rightIcon={<MdKeyboardArrowRight size={24} />}
              onClick={() => navigateToCaseStudy(activeIndex)}
              fullWidth={isSmallScreen()}
            >{caseStudies[activeIndex].buttonText}</Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
